/* iter-51f — Account-level integration connections panel.
 *
 * Renders a tidy "Connections" card listing 3rd-party integrations the
 * author has linked to their Crown & Rose account. Currently scopes
 * Canva; extensible to future providers (Dropbox, Google Drive, etc.).
 *
 * Used in two places:
 *   1. The Loom File menu → "Account & Connections…" dialog
 *   2. Bookstore account page (/bookstore/account) as an inline section
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plug, Link2, AlertCircle, Palette } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ConnectionsPanel({ heading = 'Connections', compact = false }) {
  return (
    <div className="flex flex-col gap-3" data-testid="connections-panel">
      <div>
        <h3 className="text-[12px] font-semibold tracking-tight">{heading}</h3>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Link third-party design tools so your work lives in your own accounts.
        </p>
      </div>
      <CanvaConnectionRow compact={compact} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Canva row

export function CanvaConnectionRow({ compact = false }) {
  const [status, setStatus] = useState(null);   // {configured, connected, redirect_uri}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/canva/status`);
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Listen for OAuth completion from the popup tab
  useEffect(() => {
    const expectedOrigin = window.location.origin;
    const onMsg = (ev) => {
      if (ev.origin !== expectedOrigin) return;            // iter-51h
      if (ev.data?.type === 'canva_oauth') {
        if (ev.data.success) toast.success('Canva connected.');
        else                   toast.error('Canva connection failed.');
        load();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/canva/connect`);
      const url = r.data?.url;
      if (!url) throw new Error('no url');
      window.open(url, 'canva_oauth', 'width=720,height=820,noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Canva auth.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Canva from your Crown & Rose account?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/canva/disconnect`);
      toast.success('Canva disconnected.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2.5 text-[12px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Canva connection…
      </div>
    );
  }

  // Logo / brand strip
  const Header = (
    <div className="flex items-center gap-2.5">
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-[#7d2ae8] to-[#00c4cc] text-white shadow-sm">
        <Palette className="h-4 w-4" />
      </div>
      <div className="leading-tight">
        <div className="text-[12.5px] font-semibold">Canva</div>
        <div className="text-[10.5px] text-muted-foreground">
          Design covers in your own Canva account; PNGs sync to your project cover art.
        </div>
      </div>
    </div>
  );

  if (!status.configured) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="canva-row-not-configured">
        {Header}
        {/* iter-53j — Canva enterprise approval is pending. Show the
         *  user the alternatives that work TODAY rather than admin
         *  jargon. Admin-only hint (redirect URI for when keys arrive)
         *  moved to the admin Keys panel. */}
        <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-amber-950">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="space-y-1.5">
            <div className="font-semibold">Canva integration is on the way.</div>
            <div>
              We're waiting on Canva's enterprise-tier approval before we
              can switch this on. In the meantime, you have two great ways
              to make a cover:
            </div>
            <ul className="ml-3 list-disc space-y-0.5 text-[11px]">
              <li>
                <strong>AI cover generator</strong> — open any book and
                click <em>Generate cover</em>; our built-in image model
                (Gemini Nano Banana) returns a cover in ~5 seconds. Free
                while you're testing.
              </li>
              <li>
                <strong>Upload your own</strong> — design the cover in
                Canva (or any tool) and upload the PNG/JPG via the book's
                <em> Cover art </em> panel. Auto-thumbnails and ISBN
                metadata are handled for you.
              </li>
            </ul>
            <div className="text-[10.5px] text-amber-900/80">
              We'll email you the moment Canva approves the integration —
              your designs and credits transfer automatically.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status.connected) {
    return (
      <div className={`rounded-md border border-emerald-300/60 bg-emerald-50 ${compact ? 'p-2.5' : 'p-3'}`}
            data-testid="canva-row-connected">
        <div className="flex items-center justify-between gap-3">
          {Header}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-white px-2 py-[1.5px] text-[10px] font-semibold text-emerald-900">
              <Link2 className="h-3 w-3" /> Connected
            </span>
            <button
              type="button"
              onClick={disconnect}
              disabled={busy}
              data-testid="canva-row-disconnect"
              className="inline-flex items-center gap-1 rounded-sm border border-emerald-400 bg-white px-2 py-1 text-[10.5px] hover:bg-emerald-100 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Disconnect'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-md border border-border bg-card ${compact ? 'p-2.5' : 'p-3'}`} data-testid="canva-row-disconnected">
      <div className="flex items-center justify-between gap-3">
        {Header}
        <button
          type="button"
          onClick={connect}
          disabled={busy}
          data-testid="canva-row-connect"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
          Log in to Canva
        </button>
      </div>
    </div>
  );
}
